import React, { useState, useEffect } from "react";
import { 
  FileText, 
  Navigation, 
  MapPin, 
  Clock, 
  Search, 
  Calendar, 
  Download 
} from "lucide-react";
import axios from "../api/axios"; // Assuming axios is set up for API calls

export default function Report() {
  const [imei, setImei] = useState(""); // IMEI input
  const [selectedDate, setSelectedDate] = useState("2026-01-07"); // Default selected date
  const [stats, setStats] = useState({
    distance: { day: "0 km", month: "0 km", total: "0 km" },
    trips: { day: "0", month: "0", total: "0" },
    time: { day: "0h 0m", month: "0h 0m", total: "0h 0m" }
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Function to fetch stats
  const fetchStats = async () => {
    setLoading(true); // Start loading

    try {
      // Format start and end date for the selected day
      const start_date = `${selectedDate}T00:00:00.000Z`; // Start of the day
      const end_date = `${selectedDate}T23:59:59.000Z`;   // End of the day

      const payload = {
        imei,
        start_time: start_date,
        end_time: end_date,
        page: 1,  // Page number (can be extended later for pagination)
        page_size: 25  // Number of items per page (you can adjust this based on your needs)
      };

      const response = await axios.post("http://127.0.0.1:8000/logs/report", payload);
      const data = response.data;

      // Update stats state based on the fetched data
      setStats({
        distance: {
          day: `${data.total_mileage_day} km`,
          month: `${data.total_mileage_month} km`,
          total: `${data.total_mileage} km`
        },
        trips: {
          day: `${data.total_trips_day}`,
          month: `${data.total_trips_month}`,
          total: `${data.total_trips}`
        },
        time: {
          day: `${data.total_driving_time_day}`,
          month: `${data.total_driving_time_month}`,
          total: `${data.total_driving_time}`
        }
      });
    } catch (error) {
      setErrorMsg("Failed to fetch stats");
    } finally {
      setLoading(false);  // Stop loading
    }
  };

  // useEffect to trigger fetchStats when filters change
  useEffect(() => {
    if (imei && selectedDate) {
      fetchStats();  // Call fetchStats whenever filters change
    }
  }, []);  // Dependencies array

  // Stat Card component
  const StatCard = ({ title, value, label, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col space-y-2 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">{label}</p>
          <h3 className="text-2xl font-bold text-gray-800 mt-1">{value}</h3>
        </div>
        <div className={`p-3 rounded-lg ${color} bg-opacity-10 text-purple-600`}>
          <Icon size={24} />
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-2 font-medium">{title}</p>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-8 py-4 flex justify-between items-center shadow-sm z-10">
          <div className="flex items-center space-x-4">
            <div className="bg-[#4b2c91] p-2 rounded-lg text-white">
              <FileText size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">WEBCHECK REPORT</h2>
              <p className="text-xs text-gray-500 font-medium">Hệ thống thống kê hành trình thiết bị</p>
            </div>
          </div>
        </header>

        {/* Filter Bar */}
        <main className="flex-1 overflow-y-auto p-6 md:p-10">
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-8 flex flex-wrap items-center gap-4">
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Nhập IMEI hoặc tên thiết bị..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm transition-all"
                value={imei}
                onChange={(e) => setImei(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
              <Calendar size={18} className="text-purple-600" />
              <input
                type="date"
                className="bg-transparent border-none text-sm focus:outline-none text-gray-700 font-medium"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)} // Update selected date
              />
            </div>
            <button
              onClick={fetchStats}  // Trigger fetchStats manually
              className="bg-[#4b2c91] hover:bg-[#3a2170] text-white px-8 py-2.5 rounded-lg font-bold transition-colors shadow-md text-sm flex items-center space-x-2"
            >
              <Search size={16} />
              <span>TRUY XUẤT</span>
            </button>
            <button className="flex items-center space-x-2 text-purple-700 hover:bg-purple-50 px-4 py-2.5 rounded-lg border border-purple-200 transition-all text-sm font-bold">
              <Download size={16} />
              <span>XUẤT FILE EXCEL</span>
            </button>
          </div>

          {/* Report Sections */}
          <div className="max-w-8xl mx-auto space-y-12">
            {/* Section 1: Quãng đường */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <Navigation size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 tracking-tight">Thống kê Quãng đường</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Trong ngày hôm nay" value={stats.distance.day} label="Quãng đường ngày" icon={Navigation} color="bg-blue-500" />
                <StatCard title="Tháng hiện tại (Tháng 01/2026)" value={stats.distance.month} label="Quãng đường tháng" icon={Navigation} color="bg-indigo-500" />
                <StatCard title="Kể từ khi kích hoạt thiết bị" value={stats.distance.total} label="Tổng quãng đường" icon={Navigation} color="bg-purple-500" />
              </div>
            </section>

            {/* Section 2: Chuyến đi */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                  <MapPin size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 tracking-tight">Thống kê Chuyến đi</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Các chuyến thực hiện trong ngày" value={stats.trips.day} label="Số chuyến ngày" icon={MapPin} color="bg-green-500" />
                <StatCard title="Tổng chuyến trong tháng này" value={stats.trips.month} label="Số chuyến tháng" icon={MapPin} color="bg-teal-500" />
                <StatCard title="Tổng số chuyến từ trước đến nay" value={stats.trips.total} label="Tổng số chuyến đi" icon={MapPin} color="bg-emerald-500" />
              </div>
            </section>

            {/* Section 3: Thời gian lái */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                  <Clock size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 tracking-tight">Thống kê Thời gian lái xe</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Thời gian vận hành hôm nay" value={stats.time.day} label="Thời gian ngày" icon={Clock} color="bg-orange-500" />
                <StatCard title="Tổng thời gian vận hành tháng" value={stats.time.month} label="Thời gian tháng" icon={Clock} color="bg-red-500" />
                <StatCard title="Tổng tích lũy thời gian lái" value={stats.time.total} label="Tổng thời gian lái" icon={Clock} color="bg-rose-500" />
              </div>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 p-4 text-center text-gray-400 text-xs">
          Hệ thống giám sát hành trình WEBCHECK © 2026 - Bảo mật dữ liệu thiết bị đầu cuối
        </footer>
      </div>
    </div>
  );
}
